import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/** 批次操作列。
 *
 * 固定在畫面底部而不是塞在卡片標題裡：清單動輒上千筆，使用者是「邊捲邊勾」，
 * 操作按鈕必須一直在手邊。手機版要讓開底部導覽列（64px + 安全區），
 * 否則會蓋住導覽或被導覽蓋住。
 *
 * 「選取全部符合篩選的」是這個元件存在的主因：分頁一次只看得到 40–50 筆，
 * 但實際要處理的是好幾千筆。勾滿整頁後才提示這個選項，避免使用者誤觸。
 */
export function BulkBar({
  count,
  total,
  allMatching,
  pageAllSelected,
  onSelectAllMatching,
  onClear,
  children,
  busy,
}: {
  /** 目前勾選的筆數（allMatching 時不適用） */
  count: number;
  /** 符合目前篩選的總筆數 */
  total: number;
  /** 是否已選取「全部符合篩選的」 */
  allMatching: boolean;
  /** 當頁是否已全選（決定要不要提示跨頁全選） */
  pageAllSelected: boolean;
  onSelectAllMatching: () => void;
  onClear: () => void;
  /** 操作按鈕 */
  children: ReactNode;
  busy?: boolean;
}) {
  const visible = allMatching || count > 0;
  const barRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!visible || !bar) return;
    const update = () => document.documentElement.style.setProperty('--companion-bulk-height', `${bar.getBoundingClientRect().height}px`);
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    update();
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--companion-bulk-height');
    };
  }, [visible]);
  if (!visible) return null;

  return (
    <div
      ref={barRef}
      className="bulk-bar fixed bottom-0 z-40
                 border-t border-border bg-card/95 px-4 py-3
                 shadow-[0_-8px_24px_-16px_rgba(0,0,0,0.45)] backdrop-blur
                 pb-[calc(0.75rem+64px+env(safe-area-inset-bottom))]
                 md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
      role="region"
      aria-label="批次操作"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-[13px] font-semibold tabular-nums">
            {allMatching ? `已選取全部 ${total.toLocaleString()} 筆` : `已選 ${count} 筆`}
          </span>
          {/* 只在「當頁全勾且還有下一頁」時才提示，避免一勾就跳出干擾 */}
          {!allMatching && pageAllSelected && total > count && (
            <Button size="sm" variant="ghost" onClick={onSelectAllMatching}>
              選取全部 {total.toLocaleString()} 筆
            </Button>
          )}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {children}
          <Button size="sm" variant="ghost" onClick={onClear} disabled={busy} aria-label="取消選取">
            <X className="mr-1 h-3.5 w-3.5" />
            取消選取
          </Button>
        </div>
      </div>
    </div>
  );
}

/** 清單列用的勾選框。原生 checkbox 配色票，不另外造輪子。 */
export function RowCheckbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label
      className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
        className="h-4 w-4 shrink-0 cursor-pointer accent-primary"
      />
    </label>
  );
}
